/**
 * @file Contributor UI — BackgroundRawMaterials.
 * Background Raw Materials Component
 * Handles the raw materials section with dynamic groups
 */

import React, { useMemo } from "react";
import { Trash2 } from "lucide-react";
import { UnitOption } from "./BackgroundFieldRenderer";
import { formatUnitSymbol } from "../../../../../../utils/formatUnitSymbol";

interface BackgroundRawMaterialsProps {
  rawMaterialGroups: number[];
  values: Record<string, string | File>;
  rawMaterials: { id: string; name: string }[];
  allUnits: UnitOption[];
  /** Same unit property as Finished Material Declared Quantity Unit (subMaterialUnit); from CMS `unitPropertyId`. */
  unitPropertyId: number | null | undefined;
  onAddGroup: () => void;
  onRemoveGroup: (index: number) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  /** Dataset Overview: show values only, no add/remove. */
  readonly?: boolean;
  errors?: Record<string, string>;
}

const BackgroundRawMaterials: React.FC<BackgroundRawMaterialsProps> = ({
  rawMaterialGroups,
  values,
  rawMaterials,
  allUnits,
  unitPropertyId,
  onAddGroup,
  onRemoveGroup,
  onChange,
  readonly = false,
  errors = {},
}) => {
  const unitOptions = useMemo(() => {
    if (unitPropertyId == null) return allUnits;
    return allUnits.filter(
      (u) => Number(u.propertyId) === Number(unitPropertyId)
    );
  }, [allUnits, unitPropertyId]);

  return (
    <div key="raw-materials-section" className="raw-materials-section">
      <h3 className="section-title">Raw Materials</h3>

      {rawMaterialGroups.map((groupIndex, idx) => {
        const key = `rawMaterial_${String(groupIndex).padStart(2, "0")}`;
        const qtyKey = `${key}_qty`;
        const unitKey = `rawMaterialUnit_${String(groupIndex).padStart(2, "0")}`;
        const rawMaterialNo = String(idx + 1).padStart(2, "0");

        return (
          <div key={groupIndex} className="raw-material-block">
            <div className="raw-material-row">
            {/* Raw Material */}
            <div className={`rm-cell ${errors[key] ? "field-has-error" : ""}`}>
              <label className="raw-material-primary-label">
                Raw Material {rawMaterialNo}
                <span className="required">*</span>
              </label>
              <select
                name={key}
                value={(values[key] as string) || ""}
                onChange={onChange}
                disabled={readonly || !values.materialId}
              >
                <option value="">Select Raw Material...</option>
                {rawMaterials.map((rm) => (
                  <option key={rm.id} value={rm.name}>
                    {rm.name}
                  </option>
                ))}
              </select>
              {errors[key] ? (
                <span className="error-message">{errors[key]}</span>
              ) : null}
            </div>

            {/* Quantity */}
            <div className={`rm-cell ${errors[qtyKey] ? "field-has-error" : ""}`}>
              <label>
                Qty
                <span className="required">*</span>
              </label>
              <input
                type="number"
                name={qtyKey}
                value={(values[qtyKey] as string) || ""}
                onChange={onChange}
                min="0"
                disabled={readonly}
                readOnly={readonly}
                className={readonly ? "readonly-input" : undefined}
              />
              {errors[qtyKey] ? (
                <span className="error-message">{errors[qtyKey]}</span>
              ) : null}
            </div>

            {/* Unit */}
            <div className={`rm-cell ${errors[unitKey] ? "field-has-error" : ""}`}>
              <label>
                Unit
                <span className="required">*</span>
              </label>
              <select
                name={unitKey}
                value={(values[unitKey] as string) || ""}
                onChange={onChange}
                disabled={readonly}
              >
                <option value="">Select Unit...</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {formatUnitSymbol(u.unitSymbol)}
                    {u.displayName ? ` (${u.displayName})` : ""}
                  </option>
                ))}
              </select>
              {errors[unitKey] ? (
                <span className="error-message">{errors[unitKey]}</span>
              ) : null}
            </div>

            {/* Delete */}
            <div className="rm-cell rm-delete-cell">
              <span className="rm-delete-label-spacer" aria-hidden="true">
                &nbsp;
              </span>
              {!readonly && idx > 0 ? (
                <button
                  className="rm-delete-btn"
                  type="button"
                  onClick={() => onRemoveGroup(idx)}
                  aria-label={`Remove raw material ${rawMaterialNo}`}
                  title="Remove raw material"
                >
                  <Trash2 size={16} strokeWidth={2} aria-hidden />
                </button>
              ) : (
                <span className="rm-delete-placeholder" aria-hidden="true" />
              )}
            </div>
            </div>
          </div>
        );
      })}

      {!readonly && (
        <button
          type="button"
          className="add-raw-material-btn"
          onClick={onAddGroup}
          disabled={rawMaterialGroups.length >= 10}
        >
          ➕ Add Raw Material
        </button>
      )}
    </div>
  );
};

/** React component — Background Raw Materials. */
export default BackgroundRawMaterials;

